import ReplyOutlinedIcon from "@mui/icons-material/ReplyOutlined"
import SendRoundedIcon from "@mui/icons-material/SendRounded"
import LoadingButton from "@mui/lab/LoadingButton"
import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import Chip from "@mui/material/Chip"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import InputBase from "@mui/material/InputBase"
import Typography from "@mui/material/Typography"
import { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Row from "../../../common/components/Row"
import DialogHeader from "../components/DialogHeader"
import EmailWYSWIGEditor, {
  useEmailWYSWIGEditorState,
} from "../components/EmailWYSWIGEditor"
import SelectCampaignAutoComplete, {
  useSelectCampaignAutoCompleteState,
} from "../components/SelectCampaignAutoComplete"
import {
  CONCIERGE_EMAIL_SIGNATURE_API_URL,
  buildSignaturePreviewHtml,
  isEditorBodyEmpty,
  parseIncludeSignature,
} from "../components/emailSignatureUtils"
import { getEmailEditorHTML } from "../email-campaigns/edit-email-mixin"
import { useDialogContainer } from "../utils"
import { SETTINGS_TABS, createSettingsTabURL } from "./index"

function templateBody(campaign, candidateId) {
  if (!campaign) return ""
  if (campaign.rendered_body) {
    return campaign.rendered_body
  }
  const rawBody = campaign.html_body || ""
  return rawBody
}

function draftPreview(html) {
  return (html || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim()
}

export default function InboxReplyComposer({
  thread,
  mailboxConnected,
  isReplying,
  templatesRevision = 0,
  onManageTemplates,
  onSend,
}) {
  const axios = useAuthorizedAxiosClient()
  const navigate = useNavigate()
  const Container = useDialogContainer()
  const candidateId = thread?.candidate_id
  const params = new URLSearchParams()
  if (candidateId) params.set("candidate_id", String(candidateId))
  if (templatesRevision) params.set("r", String(templatesRevision))
  const query = params.toString()
  const campaignsAPIURL = `/crm/api/zoho-mail/inbox/templates/${
    query ? `?${query}` : ""
  }`
  const {
    availableCampaigns,
    setAvailableCampaigns,
    selectedCampaign,
    setSelectedCampaign,
    campaignInputValue,
    setCampaignInputValue,
    campaignsLoaded,
    campaignsLoading,
  } = useSelectCampaignAutoCompleteState(Boolean(thread), campaignsAPIURL)
  const [expanded, setExpanded] = useState(false)
  const {
    editorReady,
    setEditorReady,
    insertLinkDialogOpen,
    setInsertLinkDialogOpen,
    editorRef,
    setEditorBody,
  } = useEmailWYSWIGEditorState(expanded)
  const [draftHtml, setDraftHtml] = useState("")
  const [signaturePreviewHtml, setSignaturePreviewHtml] = useState("")
  const [signatureLoaded, setSignatureLoaded] = useState(false)
  const appliedTemplateId = useRef(null)
  const emailSettingsURL = createSettingsTabURL(SETTINGS_TABS.configureEmail)

  const templateIncludesSignature = selectedCampaign
    ? parseIncludeSignature(selectedCampaign.include_signature)
    : false
  const signatureRequiredButMissing =
    templateIncludesSignature && signatureLoaded && !signaturePreviewHtml

  useEffect(() => {
    if (!expanded) {
      setSignatureLoaded(false)
      return
    }
    axios
      .get(CONCIERGE_EMAIL_SIGNATURE_API_URL)
      .then(({ data }) =>
        setSignaturePreviewHtml(buildSignaturePreviewHtml(data))
      )
      .catch(() => setSignaturePreviewHtml(""))
      .finally(() => setSignatureLoaded(true))
  }, [axios, expanded])

  useEffect(() => {
    if (!expanded || !editorReady || !signatureLoaded) return
    if (
      selectedCampaign?.id &&
      appliedTemplateId.current !== selectedCampaign.id
    ) {
      const includeSig = parseIncludeSignature(
        selectedCampaign.include_signature
      )
      if (includeSig && !signaturePreviewHtml) {
        navigate(emailSettingsURL)
        setSelectedCampaign(null)
        setCampaignInputValue("")
        appliedTemplateId.current = null
        return
      }
      appliedTemplateId.current = selectedCampaign.id
      const html = templateBody(selectedCampaign, candidateId)
      setEditorBody(html, includeSig, signaturePreviewHtml)
      setDraftHtml(html)
      return
    }
    if (draftHtml) {
      setEditorBody(draftHtml, templateIncludesSignature, signaturePreviewHtml)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    expanded,
    editorReady,
    signatureLoaded,
    selectedCampaign?.id,
    candidateId,
    signaturePreviewHtml,
  ])

  function snapshotDraft() {
    if (editorRef.current) {
      setDraftHtml(getEmailEditorHTML(editorRef))
    }
  }

  function handleExpand() {
    if (!mailboxConnected) return
    setExpanded(true)
  }

  function handleCloseComposer() {
    snapshotDraft()
    setExpanded(false)
  }

  function handleSend() {
    if (!editorRef.current || signatureRequiredButMissing) return
    const html = getEmailEditorHTML(editorRef)
    if (isEditorBodyEmpty(html)) return
    Promise.resolve(onSend(html, selectedCampaign?.id)).then((sent) => {
      if (sent === false) return
      appliedTemplateId.current = null
      setSelectedCampaign(null)
      setCampaignInputValue("")
      setDraftHtml("")
      setEditorBody("", false, "")
      setExpanded(false)
    })
  }

  const canSend =
    mailboxConnected &&
    editorReady &&
    !isEditorBodyEmpty(draftHtml) &&
    !signatureRequiredButMissing
  const preview = draftPreview(draftHtml)

  return (
    <>
      <Row
        component="button"
        type="button"
        onClick={handleExpand}
        disabled={!mailboxConnected}
        aria-expanded={expanded}
        aria-label="Write a reply"
        sx={{
          appearance: "none",
          WebkitAppearance: "none",
          width: "100%",
          alignItems: "center",
          gap: 1.25,
          px: 1.75,
          py: 1.25,
          m: 0,
          color: "inherit",
          border: "1px solid",
          borderColor: "colors.gray200",
          borderRadius: 2,
          bgcolor: mailboxConnected ? "background.paper" : "colors.gray50",
          cursor: mailboxConnected ? "text" : "not-allowed",
          textAlign: "left",
          font: "inherit",
          "&:hover": mailboxConnected
            ? { borderColor: "colors.gray300", bgcolor: "colors.gray50" }
            : undefined,
        }}
      >
        <ReplyOutlinedIcon
          sx={{ fontSize: 20, color: "colors.gray500", flexShrink: 0 }}
        />
        <InputBase
          readOnly
          fullWidth
          value={preview}
          placeholder={
            mailboxConnected
              ? preview
                ? "Continue reply…"
                : "Write a reply…"
              : "Connect the mailbox to reply"
          }
          sx={{
            pointerEvents: "none",
            "& input": {
              cursor: mailboxConnected ? "text" : "not-allowed",
              p: 0,
              fontSize: 14,
              color: preview ? "text.primary" : "colors.gray500",
            },
          }}
        />
      </Row>
      <Container
        open={expanded}
        onClose={handleCloseComposer}
        anchor="right"
        PaperProps={{
          sx: { width: { xs: "100%", sm: 520 }, maxWidth: "100%" },
        }}
      >
        <DialogHeader
          title="Reply"
          onClose={handleCloseComposer}
          actions={
            <Button
              size="small"
              variant="outlined"
              onClick={onManageTemplates}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Manage templates
            </Button>
          }
        />
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            pt: 2.5,
          }}
        >
          <Typography variant="body2" sx={{ color: "colors.gray600" }}>
            {thread?.subject
              ? `Replying to “${thread.subject}”`
              : "Write a reply for this conversation."}
          </Typography>
          <SelectCampaignAutoComplete
            compact
            hideHelperText
            size="small"
            availableCampaigns={availableCampaigns}
            setAvailableCampaigns={setAvailableCampaigns}
            campaignInputValue={campaignInputValue}
            setCampaignInputValue={setCampaignInputValue}
            selectedCampaign={selectedCampaign}
            setSelectedCampaign={setSelectedCampaign}
            campaignsLoaded={campaignsLoaded}
            campaignsLoading={campaignsLoading}
            createLinkLabel="Manage templates"
            groupByScope
            onCreateCampaignLinkClicked={onManageTemplates}
          />
          {selectedCampaign ? (
            <Row sx={{ alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography variant="caption" sx={{ color: "colors.gray600" }}>
                {candidateId
                  ? "Placeholders are filled for this candidate. Edits apply to this reply only."
                  : "This thread is not linked to a candidate, so placeholders will not be filled."}
              </Typography>
              {(selectedCampaign.attachment_count ||
                selectedCampaign.attachments?.length) > 0 ? (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`${
                    selectedCampaign.attachment_count ||
                    selectedCampaign.attachments.length
                  } attachment${
                    (selectedCampaign.attachment_count ||
                      selectedCampaign.attachments.length) === 1
                      ? ""
                      : "s"
                  }`}
                />
              ) : null}
              {selectedCampaign.is_global ? (
                <Chip
                  size="small"
                  variant="outlined"
                  label="Default template"
                />
              ) : null}
              {templateIncludesSignature ? (
                <Chip
                  size="small"
                  color="primary"
                  variant="outlined"
                  label="Your signature will be included"
                />
              ) : null}
            </Row>
          ) : null}
          {signatureRequiredButMissing ? (
            <Alert
              severity="warning"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => navigate(emailSettingsURL)}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  Set up signature
                </Button>
              }
            >
              This template includes a signature. Set up your email signature
              before you can send.
            </Alert>
          ) : null}
          {expanded ? (
            <EmailWYSWIGEditor
              compact
              height={280}
              editorRef={editorRef}
              editorReady={editorReady}
              setEditorReady={setEditorReady}
              insertLinkDialogOpen={insertLinkDialogOpen}
              setInsertLinkDialogOpen={setInsertLinkDialogOpen}
              includeSignature={templateIncludesSignature}
              signaturePreviewHtml={signaturePreviewHtml}
              onContentChange={setDraftHtml}
            />
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <LoadingButton
            variant="contained"
            startIcon={<SendRoundedIcon />}
            loading={isReplying}
            disabled={!canSend}
            onClick={handleSend}
            sx={{ textTransform: "none", fontWeight: 600, px: 2.5 }}
          >
            Send reply
          </LoadingButton>
        </DialogActions>
      </Container>
    </>
  )
}
